import { useState, useRef, useEffect } from "react";
import { testimonialService } from "../services/testimonialService";

// ─── Types ────────────────────────────────────────────────────────────────────
type Testimonial = {
  id: number;
  customerName: string;
  designation: string;
  location: string;
  rating: number;
  review: string;
  productName: string;
  imageUrl: string;
  imageFile: File | null;
  status: "active" | "inactive";
  featured: boolean;
  createdAt: string;
};

const API_PATH = process.env.REACT_APP_API_PATH;

// ─── Seed Data ────────────────────────────────────────────────────────────────
const SEED: Testimonial[] = [
  {
    id: 1,
    customerName: "Priya Sharma",
    designation: "Bride",
    location: "Delhi",
    rating: 5,
    review:
      "Absolutely loved the bridal set! The craftsmanship is exquisite and the diamonds sparkle beautifully. Every guest at my wedding was asking about my jewellery. Highly recommend Nine Sisters!",
    productName: "Polki Bridal Necklace Set",
    imageUrl: "",
    imageFile: null,
    status: "active",
    featured: true,
    createdAt: "10 May 2026",
  },
  {
    id: 2,
    customerName: "Anjali Mehta",
    designation: "Customer",
    location: "Mumbai",
    rating: 5,
    review:
      "The diamond earrings are simply stunning. Packaging was beautiful and delivery was on time. The quality exceeded my expectations. Will definitely order again!",
    productName: "Diamond Gold Earrings",
    imageUrl: "",
    imageFile: null,
    status: "active",
    featured: false,
    createdAt: "08 May 2026",
  },
  {
    id: 3,
    customerName: "Sunita Agarwal",
    designation: "Regular Customer",
    location: "Jaipur",
    rating: 4,
    review:
      "Beautiful collection and great customer service. The custom wedding ring came out exactly as I imagined. Small delay in delivery but overall a wonderful experience.",
    productName: "Custom Wedding Ring",
    imageUrl: "",
    imageFile: null,
    status: "active",
    featured: false,
    createdAt: "05 May 2026",
  },
  {
    id: 4,
    customerName: "Ritu Kapoor",
    designation: "Jewellery Enthusiast",
    location: "Bengaluru",
    rating: 5,
    review:
      "I've bought from many jewellery brands but Nine Sisters is on a different level. The temple gold earrings are authentic and the finish is impeccable. 10/10!",
    productName: "Temple Gold Earrings",
    imageUrl: "",
    imageFile: null,
    status: "inactive",
    featured: false,
    createdAt: "02 May 2026",
  },
];

// ─── Star Rating Component ────────────────────────────────────────────────────
function StarRating({
  value,
  onChange,
  readOnly = false,
  size = 16,
}: {
  value: number;
  onChange?: (v: number) => void;
  readOnly?: boolean;
  size?: number;
}) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={(readOnly ? star <= value : star <= (hovered || value)) ? "#f59e0b" : "none"}
          stroke="#f59e0b"
          strokeWidth="2"
          style={{ cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}
          onMouseEnter={() => !readOnly && setHovered(star)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          onClick={() => !readOnly && onChange?.(star)}
        >
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ))}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
type ModalMode = "none" | "add" | "edit" | "view";

const emptyForm = (): Omit<Testimonial, "id" | "createdAt"> => ({
  customerName: "",
  designation: "",
  location: "",
  rating: 5,
  review: "",
  productName: "",
  imageUrl: "",
  imageFile: null,
  status: "active",
  featured: false,
});

// ─── Main Page ────────────────────────────────────────────────────────────────
export function TestimonialsPage() {
  const [items, setItems] = useState<Testimonial[]>(SEED);
  const [modalMode, setModalMode] = useState<ModalMode>("none");
  const [selected, setSelected] = useState<Testimonial | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [filter, setFilter] = useState<"all" | "active" | "inactive" | "featured">("all");
  const [search, setSearch] = useState("");
  const imageRef = useRef<HTMLInputElement>(null);

  function f<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function openAdd() {
    setForm(emptyForm());
    setModalMode("add");
  }
  function openEdit(t: Testimonial) {
    setForm({
      customerName: t.customerName,
      designation: t.designation,
      location: t.location,
      rating: t.rating,
      review: t.review,
      productName: t.productName,
      imageUrl: t.imageUrl,
      imageFile: null,
      status: t.status,
      featured: t.featured,
    });
    setSelected(t);
    setModalMode("edit");
  }
  function openView(t: Testimonial) {
    setSelected(t);
    setModalMode("view");
  }
  function closeModal() {
    setModalMode("none");
    setSelected(null);
    if (imageRef.current) imageRef.current.value = "";
  }

  function handleImageFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    f("imageFile", file);
    const reader = new FileReader();
    reader.onload = (ev) => f("imageUrl", ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  useEffect(() => {

    loadtestimonial();

  }, []);

  async function loadtestimonial() {

    try {

      const data =
        await testimonialService
          .GetClientTestimonials();

      console.log(
        data,
        "Load testimonial"
      );

      const mapped: Testimonial[] =
        data.map((b: any) => ({

          id: b.id,

          customerName:
            b.customerName || "",

          designation:
            b.designation || "",

          location:
            b.location || "",

          rating:
            b.rating || 0,

          review:
            b.review || "",

          productName:
            b.productName || "",

          imageFile: null,

          featured:
            b.featured || false,

          createdAt:
            b.createdAt || "",

          imageUrl:
            b.customerImage
              ? `${API_PATH}/TestimonialImages/${b.customerImage}`
              : "",

          status: b.isActive ? "active" : "inactive",
        }));

      setItems(mapped);

    }
    catch (error) {

      console.error(
        "Failed to load testimonial",
        error
      );
    }
  }



  async function save() {

    if (!form.customerName.trim() || !form.review.trim()) return;

    try {

      const payload = new FormData();

      payload.append(
        "ID",
        modalMode === "edit"
          ? String(selected?.id)
          : "0"
      );

      payload.append("CustomerName", form.customerName);

      payload.append("Designation", form.designation);

      payload.append("Location", form.location);

      payload.append("ProductName", form.productName);

      payload.append("Rating", String(form.rating));

      payload.append("Review", form.review);

      payload.append(
        "Status",
        form.status === "active"
          ? "true"
          : "false"
      );

      payload.append(
        "Featured",
        form.featured
          ? "true"
          : "false"
      );

      // IMAGE
      if (form.imageFile) {

        payload.append(
          "ImageFile",
          form.imageFile
        );
      }

      // =========================
      // ADD
      // =========================
      if (modalMode === "add") {

        await testimonialService
          .AddClienttestimonial(payload);

      }

      // =========================
      // UPDATE
      // =========================
      else if (
        modalMode === "edit" &&
        selected
      ) {

        await testimonialService
          .UpdateClienttestimonial(payload);
      }

      // RELOAD LIST
      await loadtestimonial();

      closeModal();

    }
    catch (error) {

      console.error(
        "Save testimonial failed",
        error
      );
    }
  }

  async function deleteItem(id: number) {

    if (!window.confirm("Delete this testimonial?")) {
      return;
    }

    try {

      await testimonialService
        .DeleteClienttestimonial(id);

      // reload data
      await loadtestimonial();

    }
    catch (error) {

      console.error(
        "Delete testimonial failed",
        error
      );
    }
  }

  function toggleStatus(id: number) {
    setItems((p) =>
      p.map((t) =>
        t.id === id ? { ...t, status: t.status === "active" ? "inactive" : "active" } : t
      )
    );
  }

  function toggleFeatured(id: number) {
    setItems((p) => p.map((t) => (t.id === id ? { ...t, featured: !t.featured } : t)));
  }

  const filtered = items.filter((t) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "active" && t.status === "active") ||
      (filter === "inactive" && t.status === "inactive") ||
      (filter === "featured" && t.featured);
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      t.customerName.toLowerCase().includes(q) ||
      t.productName.toLowerCase().includes(q) ||
      t.review.toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: items.length,
    active: items.filter((t) => t.status === "active").length,
    featured: items.filter((t) => t.featured).length,
    avgRating: items.length ? (items.reduce((a, t) => a + t.rating, 0) / items.length).toFixed(1) : "0",
  };

  const initials = (name: string) =>
    name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <>
      <style>{`
        /* ── Stats ── */
        .tm-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
        .tm-stat { background:#fff; border:1px solid #f1f5f9; border-radius:14px; padding:16px 18px; display:flex; align-items:center; gap:14px; }
        .tm-stat-icon { width:42px; height:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
        .tm-stat-val { font-size:22px; font-weight:800; color:#1e293b; line-height:1; }
        .tm-stat-label { font-size:12px; color:#94a3b8; font-weight:600; margin-top:2px; }

        /* ── Toolbar ── */
        .tm-toolbar { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:16px; }
        .tm-search { flex:1; min-width:200px; position:relative; }
        .tm-search input { width:100%; padding:9px 12px 9px 36px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:13px; outline:none; box-sizing:border-box; }
        .tm-search input:focus { border-color:#b45309; box-shadow:0 0 0 3px rgba(180,83,9,.1); }
        .tm-search-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#94a3b8; pointer-events:none; }
        .tm-filter-tabs { display:flex; gap:4px; background:#f8fafc; border-radius:10px; padding:4px; }
        .tm-filter-tab { padding:6px 14px; border-radius:8px; border:none; font-size:12px; font-weight:700; cursor:pointer; background:transparent; color:#64748b; transition:all .15s; }
        .tm-filter-tab.active { background:#fff; color:#b45309; box-shadow:0 1px 4px rgba(0,0,0,.1); }
        .tm-btn-primary { background:linear-gradient(135deg,#b45309,#d97706); color:#fff; border:none; border-radius:10px; padding:9px 18px; font-size:13px; font-weight:700; cursor:pointer; display:flex; align-items:center; gap:6px; white-space:nowrap; box-shadow:0 2px 8px rgba(180,83,9,.35); }

        /* ── Grid ── */
        .tm-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:16px; }
        .tm-card { background:#fff; border:1.5px solid #f1f5f9; border-radius:16px; overflow:hidden; display:flex; flex-direction:column; transition:box-shadow .2s,border-color .2s; }
        .tm-card:hover { box-shadow:0 6px 24px rgba(0,0,0,.08); border-color:#e2e8f0; }
        .tm-card-top { padding:16px 16px 0; display:flex; align-items:flex-start; gap:12px; }
        .tm-avatar { width:46px; height:46px; border-radius:50%; overflow:hidden; flex-shrink:0; background:linear-gradient(135deg,#fef3c7,#fde68a); display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:800; color:#92400e; }
        .tm-avatar img { width:100%; height:100%; object-fit:cover; }
        .tm-name { font-size:14px; font-weight:800; color:#1e293b; }
        .tm-desig { font-size:11px; color:#94a3b8; font-weight:600; }
        .tm-loc { font-size:11px; color:#64748b; margin-top:1px; }
        .tm-badges { display:flex; gap:5px; flex-wrap:wrap; margin-left:auto; flex-shrink:0; }
        .tm-badge { font-size:10px; font-weight:700; padding:3px 8px; border-radius:20px; }
        .tm-badge-featured { background:#fef3c7; color:#92400e; }
        .tm-badge-active { background:#dcfce7; color:#166534; }
        .tm-badge-inactive { background:#f1f5f9; color:#64748b; }
        .tm-card-body { padding:12px 16px 14px; flex:1; }
        .tm-review { font-size:13px; color:#475569; line-height:1.6; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
        .tm-product { font-size:11px; color:#b45309; font-weight:700; margin-top:8px; display:flex; align-items:center; gap:4px; }
        .tm-card-footer { padding:10px 16px; border-top:1px solid #f8fafc; display:flex; align-items:center; justify-content:space-between; background:#fafbff; }
        .tm-card-date { font-size:11px; color:#94a3b8; }
        .tm-card-actions { display:flex; gap:5px; }
        .tm-btn-sm { padding:5px 11px; border-radius:8px; border:1.5px solid #e2e8f0; background:#fff; font-size:11px; font-weight:700; cursor:pointer; color:#475569; transition:all .15s; }
        .tm-btn-sm:hover { border-color:#b45309; color:#b45309; }
        .tm-btn-del { border-color:#fee2e2; color:#ef4444; }
        .tm-btn-del:hover { background:#fee2e2; border-color:#ef4444; }
        .tm-toggle { position:relative; width:36px; height:20px; flex-shrink:0; }
        .tm-toggle input { opacity:0; width:0; height:0; }
        .tm-slider { position:absolute; cursor:pointer; inset:0; background:#e2e8f0; border-radius:20px; transition:.3s; }
        .tm-slider:before { content:''; position:absolute; width:14px; height:14px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:.3s; box-shadow:0 1px 3px rgba(0,0,0,.2); }
        input:checked + .tm-slider { background:#22c55e; }
        input:checked + .tm-slider:before { transform:translateX(16px); }
        .tm-empty { text-align:center; padding:60px 20px; color:#94a3b8; }
        .tm-empty-icon { font-size:48px; margin-bottom:12px; }
        .tm-empty-title { font-size:16px; font-weight:700; color:#64748b; }
        .tm-empty-sub { font-size:13px; margin-top:4px; }

        /* ── Modal ── */
        .ns-overlay { position:fixed; inset:0; background:rgba(15,23,42,.55); backdrop-filter:blur(4px); z-index:1000; display:flex; align-items:flex-start; justify-content:center; padding:24px 16px; overflow-y:auto; }
        .ns-modal { background:#fff; border-radius:20px; width:100%; max-width:580px; box-shadow:0 24px 60px rgba(0,0,0,.2); margin:auto; animation:mIn .25s ease; }
        @keyframes mIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        .ns-modal-head { display:flex; justify-content:space-between; align-items:center; padding:20px 24px; border-bottom:1px solid #f1f5f9; }
        .ns-modal-title { font-size:17px; font-weight:700; color:#1e293b; }
        .ns-modal-close { width:34px; height:34px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; font-size:16px; color:#64748b; }
        .ns-modal-body { padding:22px 24px; max-height:68vh; overflow-y:auto; }
        .ns-modal-foot { padding:16px 24px; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:10px; }
        .ns-field { margin-bottom:14px; }
        .ns-label { font-size:12px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.5px; display:block; margin-bottom:5px; }
        .ns-input { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:14px; outline:none; box-sizing:border-box; }
        .ns-input:focus { border-color:#b45309; box-shadow:0 0 0 3px rgba(180,83,9,.1); }
        .ns-textarea { resize:vertical; min-height:90px; }
        .ns-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .ns-btn-sec { background:#f8fafc; color:#475569; border:1.5px solid #e2e8f0; border-radius:10px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; }
        .ns-btn-gold { background:linear-gradient(135deg,#b45309,#d97706); color:#fff; border:none; border-radius:10px; padding:9px 20px; font-size:14px; font-weight:700; cursor:pointer; }
        .ns-check-row { display:flex; align-items:center; gap:8px; }
        .ns-check-row input[type=checkbox] { width:16px; height:16px; accent-color:#b45309; cursor:pointer; }
        .ns-check-row label { font-size:13px; font-weight:600; color:#475569; cursor:pointer; }

        /* ── Image Upload ── */
        .tm-img-box { border:2px dashed #e2e8f0; border-radius:12px; overflow:hidden; background:#fafbff; }
        .tm-img-preview { position:relative; }
        .tm-img-preview img { width:100%; height:140px; object-fit:cover; display:block; }
        .tm-img-overlay { position:absolute; inset:0; background:rgba(0,0,0,.42); opacity:0; transition:.2s; display:flex; align-items:center; justify-content:center; gap:10px; }
        .tm-img-preview:hover .tm-img-overlay { opacity:1; }
        .tm-img-overlay-btn { background:rgba(255,255,255,.92); border:none; border-radius:8px; padding:6px 14px; font-size:12px; font-weight:700; cursor:pointer; color:#1e293b; }
        .tm-img-overlay-btn.del { color:#ef4444; }
        .tm-img-empty { height:110px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; color:#94a3b8; cursor:pointer; transition:background .15s; }
        .tm-img-empty:hover { background:#f1f5f9; }
        .tm-img-empty-icon { font-size:26px; }
        .tm-img-empty-text { font-size:12px; font-weight:600; }
        .tm-img-row { display:flex; gap:8px; align-items:center; padding:10px 12px; border-top:1px solid #f1f5f9; background:#fff; }
        .tm-img-row input { flex:1; padding:7px 10px; border:1.5px solid #e2e8f0; border-radius:8px; font-size:13px; outline:none; }
        .tm-img-row input:focus { border-color:#b45309; }
        .tm-img-upload-btn { background:linear-gradient(135deg,#b45309,#d97706); color:#fff; border:none; border-radius:8px; padding:7px 14px; font-size:12px; font-weight:700; cursor:pointer; white-space:nowrap; display:flex; align-items:center; gap:5px; flex-shrink:0; }

        /* ── View Modal ── */
        .tm-view-card { background:linear-gradient(135deg,#fffbf5,#fff); border:1.5px solid #fde68a; border-radius:16px; padding:24px; }
        .tm-view-header { display:flex; gap:14px; align-items:flex-start; margin-bottom:16px; }
        .tm-view-avatar { width:60px; height:60px; border-radius:50%; overflow:hidden; flex-shrink:0; background:linear-gradient(135deg,#fef3c7,#fde68a); display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:800; color:#92400e; }
        .tm-view-avatar img { width:100%; height:100%; object-fit:cover; }
        .tm-view-name { font-size:17px; font-weight:800; color:#1e293b; }
        .tm-view-meta { font-size:12px; color:#94a3b8; font-weight:600; margin-top:2px; }
        .tm-view-review { font-size:14px; color:#475569; line-height:1.7; margin:14px 0; font-style:italic; border-left:3px solid #fde68a; padding-left:14px; }
        .tm-view-product { font-size:12px; color:#b45309; font-weight:700; }

        @media (max-width:640px) {
          .tm-stats { grid-template-columns:repeat(2,1fr); }
          .tm-grid { grid-template-columns:1fr; }
          .ns-row { grid-template-columns:1fr; }
        }
      `}</style>

      {/* ── Stats Row ── */}
      <div className="tm-stats">
        {[
          { icon: "⭐", label: "Avg Rating", val: stats.avgRating, bg: "#fef3c7", color: "#92400e" },
          { icon: "✅", label: "Active", val: stats.active, bg: "#dcfce7", color: "#166534" },
          { icon: "💎", label: "Featured", val: stats.featured, bg: "#ede9fe", color: "#5b21b6" },
          { icon: "💬", label: "Total Reviews", val: stats.total, bg: "#dbeafe", color: "#1d4ed8" },
        ].map((s) => (
          <div className="tm-stat" key={s.label}>
            <div className="tm-stat-icon" style={{ background: s.bg, color: s.color }}>
              {s.icon}
            </div>
            <div>
              <div className="tm-stat-val">{s.val}</div>
              <div className="tm-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="tm-toolbar">
        <div className="tm-search">
          <svg className="tm-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input
            placeholder="Search testimonials..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="tm-filter-tabs">
          {(["all", "active", "inactive", "featured"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`tm-filter-tab ${filter === tab ? "active" : ""}`}
              onClick={() => setFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <button className="tm-btn-primary" type="button" onClick={openAdd}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Add Testimonial
        </button>
      </div>

      {/* ── Cards Grid ── */}
      {filtered.length === 0 ? (
        <div className="tm-empty">
          <div className="tm-empty-icon">💬</div>
          <div className="tm-empty-title">No testimonials found</div>
          <div className="tm-empty-sub">Try changing the filter or search term</div>
        </div>
      ) : (
        <div className="tm-grid">
          {filtered.map((t) => (
            <div key={t.id} className="tm-card">
              <div className="tm-card-top">
                <div className="tm-avatar">
                  {t.imageUrl ? (
                    <img src={t.imageUrl} alt={t.customerName} />
                  ) : (
                    initials(t.customerName)
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="tm-name">{t.customerName}</div>
                  <div className="tm-desig">{t.designation}</div>
                  {t.location && <div className="tm-loc">📍 {t.location}</div>}
                  <StarRating value={t.rating} readOnly size={13} />
                </div>
                <div className="tm-badges">
                  {t.featured && <span className="tm-badge tm-badge-featured">⭐ Featured</span>}
                  <span className={`tm-badge ${t.status === "active" ? "tm-badge-active" : "tm-badge-inactive"}`}>
                    {t.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="tm-card-body">
                <p className="tm-review">"{t.review}"</p>
                {t.productName && (
                  <div className="tm-product">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                    {t.productName}
                  </div>
                )}
              </div>

              <div className="tm-card-footer">
                <span className="tm-card-date">{t.createdAt}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label className="tm-toggle" title={t.status === "active" ? "Active" : "Inactive"}>
                    <input
                      type="checkbox"
                      checked={t.status === "active"}
                      onChange={() => toggleStatus(t.id)}
                    />
                    <span className="tm-slider"></span>
                  </label>
                  <div className="tm-card-actions">
                    <button className="tm-btn-sm" type="button" onClick={() => openView(t)}>View</button>
                    <button className="tm-btn-sm" type="button" onClick={() => openEdit(t)}>Edit</button>
                    <button className="tm-btn-sm tm-btn-del" type="button" onClick={() => deleteItem(t.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modalMode === "add" || modalMode === "edit") && (
        <div className="ns-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="ns-modal">
            <div className="ns-modal-head">
              <span className="ns-modal-title">
                {modalMode === "add" ? "💬 Add Testimonial" : "✏️ Edit Testimonial"}
              </span>
              <button className="ns-modal-close" onClick={closeModal} type="button">✕</button>
            </div>

            <div className="ns-modal-body">
              {/* Customer Photo */}
              <div className="ns-field">
                <label className="ns-label">Customer Photo (Optional)</label>
                <div className="tm-img-box">
                  {form.imageUrl ? (
                    <div className="tm-img-preview">
                      <img src={form.imageUrl} alt="Preview" />
                      <div className="tm-img-overlay">
                        <button type="button" className="tm-img-overlay-btn" onClick={() => imageRef.current?.click()}>Change</button>
                        <button type="button" className="tm-img-overlay-btn del" onClick={() => { f("imageUrl", ""); f("imageFile", null); if (imageRef.current) imageRef.current.value = ""; }}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <div className="tm-img-empty" onClick={() => imageRef.current?.click()}>
                      <div className="tm-img-empty-icon">👤</div>
                      <div className="tm-img-empty-text">Click to upload customer photo</div>
                      <small style={{ color: "#cbd5e1", fontSize: 11 }}>PNG, JPG supported</small>
                    </div>
                  )}
                  <div className="tm-img-row">
                    <button type="button" className="tm-img-upload-btn" onClick={() => imageRef.current?.click()}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      Upload
                    </button>
                    <input
                      placeholder="or paste image URL..."
                      value={form.imageUrl.startsWith("data:") ? "" : form.imageUrl}
                      onChange={(e) => f("imageUrl", e.target.value)}
                    />
                  </div>
                </div>
                <input ref={imageRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageFile} />
              </div>

              <div className="ns-row">
                <div className="ns-field">
                  <label className="ns-label">Customer Name *</label>
                  <input className="ns-input" value={form.customerName} onChange={(e) => f("customerName", e.target.value)} placeholder="e.g. Priya Sharma" />
                </div>
                <div className="ns-field">
                  <label className="ns-label">Designation</label>
                  <input className="ns-input" value={form.designation} onChange={(e) => f("designation", e.target.value)} placeholder="e.g. Bride, Customer" />
                </div>
              </div>

              <div className="ns-row">
                <div className="ns-field">
                  <label className="ns-label">Location</label>
                  <input className="ns-input" value={form.location} onChange={(e) => f("location", e.target.value)} placeholder="e.g. Delhi, Mumbai" />
                </div>
                <div className="ns-field">
                  <label className="ns-label">Product Purchased</label>
                  <input className="ns-input" value={form.productName} onChange={(e) => f("productName", e.target.value)} placeholder="e.g. Diamond Gold Earrings" />
                </div>
              </div>

              <div className="ns-field">
                <label className="ns-label">Rating *</label>
                <StarRating value={form.rating} onChange={(v) => f("rating", v)} size={22} />
              </div>

              <div className="ns-field">
                <label className="ns-label">Review *</label>
                <textarea
                  className="ns-input ns-textarea"
                  value={form.review}
                  onChange={(e) => f("review", e.target.value)}
                  placeholder="Customer's review..."
                />
              </div>

              <div className="ns-row">
                <div className="ns-field">
                  <label className="ns-label">Status</label>
                  <select className="ns-input" value={form.status} onChange={(e) => f("status", e.target.value as Testimonial["status"])}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="ns-field" style={{ display: "flex", alignItems: "flex-end", paddingBottom: 2 }}>
                  <div className="ns-check-row">
                    <input
                      type="checkbox"
                      id="featured-chk"
                      checked={form.featured}
                      onChange={(e) => f("featured", e.target.checked)}
                    />
                    <label htmlFor="featured-chk">⭐ Mark as Featured (shows on homepage)</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="ns-modal-foot">
              <button className="ns-btn-sec" onClick={closeModal} type="button">Cancel</button>
              <button className="ns-btn-gold" onClick={save} type="button">
                {modalMode === "add"
                  ? "Add Testimonial"
                  : "Update Testimonial"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalMode === "view" && selected && (
        <div className="ns-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="ns-modal">
            <div className="ns-modal-head">
              <span className="ns-modal-title">💬 Testimonial Detail</span>
              <button className="ns-modal-close" onClick={closeModal} type="button">✕</button>
            </div>
            <div className="ns-modal-body">
              <div className="tm-view-card">
                <div className="tm-view-header">
                  <div className="tm-view-avatar">
                    {selected.imageUrl ? (
                      <img src={selected.imageUrl} alt={selected.customerName} />
                    ) : (
                      initials(selected.customerName)
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="tm-view-name">{selected.customerName}</div>
                    <div className="tm-view-meta">
                      {selected.designation}{selected.location ? ` · 📍 ${selected.location}` : ""}
                    </div>
                    <StarRating value={selected.rating} readOnly size={16} />
                  </div>
                  <div className="tm-badges">
                    {selected.featured && <span className="tm-badge tm-badge-featured">⭐ Featured</span>}
                    <span className={`tm-badge ${selected.status === "active" ? "tm-badge-active" : "tm-badge-inactive"}`}>
                      {selected.status}
                    </span>
                  </div>
                </div>
                <div className="tm-view-review">{selected.review}</div>
                {selected.productName && (
                  <div className="tm-view-product">
                    💍 Product: {selected.productName}
                  </div>
                )}
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>Added: {selected.createdAt}</div>
              </div>
            </div>
            <div className="ns-modal-foot">
              <button className="ns-btn-sec" onClick={closeModal} type="button">Close</button>
              <button className="ns-btn-gold" onClick={() => { closeModal(); openEdit(selected); }} type="button">Edit</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
